import {Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState} from "react";
import {SendEmailForm} from "./SendEmailsPage.tsx";
import {Alert, Box, Button, Checkbox, List, ListItem, ListItemText} from "@mui/material";
import {EmailContacts, eventerConsoleApiClient} from "../../../../../api-client/EventerConsoleApiClient.ts";
import {useAccount} from "../../Accounts.tsx";
import DialogActionButton from "../../../../../../appfw/ui/dialogs/DialogActionButton.tsx";
import {RemoveCircle} from "@mui/icons-material";
import {TextInputDialog} from "../../../../../../appfw/ui/dialogs/TextInputDialog.tsx";
import {isValidEmail} from "../../../../../../appfw/utils/EmailUtil.ts";

interface RecipientsChooserStepProps {
    control: [SendEmailForm, Dispatch<SetStateAction<SendEmailForm>>],
}

export default function RecipientsChooserStep(props: RecipientsChooserStepProps) {

    const account = useAccount()

    const [form, setForm] = props.control
    const [emailContacts, setEmailContacts] = useState<undefined | EmailContacts>()

    const [addRecipientDialogOpen, setAddRecipientDialogOpen] = useState(false)

    const onAddRecipientDialogClose = useCallback(async (email: string | null) => {
        if (email != null) {

            await eventerConsoleApiClient.addEmailContacts({
                accountId: account.id,
                emailContacts: {emailContacts: [{email: email}]},
            })

            if (form.emailAddresses !== undefined) {
                setForm({
                    ...form,
                    emailAddresses: [...form.emailAddresses, email]
                })
            }
            if (emailContacts?.emailContacts !== undefined) {
                setEmailContacts(
                    {
                        emailContacts: [...emailContacts.emailContacts, {email: email}]
                    }
                )
            }
        }

        setAddRecipientDialogOpen(false)

    }, [account.id, emailContacts, form.emailAddresses])

    const deleteEmailAddress = useCallback(async (email: string) => {
        await eventerConsoleApiClient.removeEmailContacts({
            accountId: account.id,
            emailAddresses: [email],
        })
        if (form.emailAddresses !== undefined) {
            setForm({
                ...form,
                emailAddresses: form.emailAddresses.filter((it) => it !== email)
            })
        }
        if (emailContacts?.emailContacts !== undefined) {
            setEmailContacts({
                emailContacts: emailContacts.emailContacts
                    .filter((it) => it.email !== email)
            })
        }
    }, [account.id, emailContacts, form.emailAddresses])

    const emailControls = useMemo(() => {
        const emailAddresses = form.emailAddresses || []
        if (emailAddresses === undefined) {
            return []
        }
        if (emailContacts?.emailContacts === undefined) {
            return []
        }

        return emailContacts.emailContacts.map((emailContact) => {
            let checked = emailAddresses.includes(emailContact.email)
            return (
                <ListItem key={emailContact.email}
                          secondaryAction={
                              <DialogActionButton
                                  outlet={<RemoveCircle/>}
                                  dialogTitle={"Empfänger entfernen"}
                                  dialogContent={`Möchten Sie '${emailContact.email}' aus der Empfängerliste entfernen?`}
                                  dialogActions={[
                                      {label: "Nein"},
                                      {
                                          label: "Ja",
                                          action: () => deleteEmailAddress(emailContact.email)
                                      },
                                  ]}
                              />
                          }>

                    <Checkbox
                        checked={checked}
                        onChange={(_event, checked) => {
                            if (checked) {
                                setForm({
                                    ...form,
                                    emailAddresses: [...emailAddresses || [], emailContact.email],
                                })
                            } else {
                                setForm({
                                    ...form,
                                    emailAddresses: emailAddresses.filter((it) => it !== emailContact.email)
                                })
                            }
                        }}
                    />

                    <ListItemText>{emailContact.email}</ListItemText>
                </ListItem>
            )
        })
    }, [emailContacts, form.emailAddresses])

    useEffect(() => {
        let active = true

        eventerConsoleApiClient.getEmailContacts({accountId: account.id})
            .then((emailContacts) => {
                if (!active) {
                    return
                }
                setEmailContacts(emailContacts)

                if (form.emailAddresses === undefined) {
                    // first load, set all email addresses
                    setForm(
                        {...form, emailAddresses: emailContacts.emailContacts.map((it) => it.email)}
                    )
                }
            })

        return () => {
            active = false
        }
    }, [])

    if (emailContacts === undefined) {
        // loading spinner?
        return
    }

    return (
        <>
            {(form.emailAddresses?.length || 0) == 0 &&
                (
                    <Alert sx={{marginTop: 2, marginLeft: 2, marginRight: 2}} severity="info">
                        Mindestens 1 Empfänger auswählen
                    </Alert>
                )
            }

            <List>
                {emailControls}
            </List>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: 2,
                }}
            >
                <Button
                    variant={"contained"}
                    onClick={() => {
                        setAddRecipientDialogOpen(true)
                    }}
                >
                    Hinzufügen
                </Button>

                <TextInputDialog
                    open={addRecipientDialogOpen}
                    onClose={onAddRecipientDialogClose}
                    dialogTitle={"Empfänger hinzufügen"}
                    dialogContent={"Gib die E-Mail-Adresse des Empfängers ein, den du hinzufügen möchtest."}
                    inputLabel={"E-Mail"}
                    validate={isValidEmail}
                />
            </Box>
        </>
    )
}